import React, { useState, useRef } from "react";
import "./contentpage.css";
import AnimatedPopup from "../components/AnimatedPopup";
import { Link } from "react-router-dom";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
import Question from "../components/Question";

function IslamicTradePage5() {
    const contentRef = useRef(null);
  const titleText =
    " Prohibition of Disposal, Transfer or Usage before Posession (Nahī an at-Taṣarruf qabla al-Qabḍ)";
  const links = {
    prev: "/islamic-trade-page-4", 
    index: "/index",                 
    next: "/islamic-trade-page-6", 
  };

  const questions = [
    {
      text: "1. What is the Islamic ruling if the good gets destroyed by the seller after the execution of the sale while it is still in possesion of the seller?",
      options: [
        {
          value: "A",
          label: "A. The buyer is liable to offer a compensation.",
        },
        {
          value: "B",
          label: "B. The seller is liable to offer a compensation.",
        },
        {
          value: "C",
          label: "C. The trade is annuled.",
        },
        {
          value: "D",
          label: "D. None of the above.",
        },
      ],
      correct: "B",
    },
    {
      text: "2. What is the Islamic ruling if the good gets destroyed by a natural disaster after the execution of the sale while it is still in possesion of the seller?",
      options: [
        {
          value: "A",
          label: "A. The buyer is liable to offer a compensation.",
        },
        {
          value: "B",
          label: "B. The seller is liable to offer a compensation.",
        },
        { value: "C", label: "C. The trade is annuled." },
        { value: "D", label: "D. None of the above." },
      ],
      correct: "C",
    },
    {
      text: "3.  A house buyer can re-sell it even before the title is transferred to his name.",
      options: [
        { value: "A", label: "A. True" },
        { value: "B", label: "B. False" },
        { value: "C", label: "C. Maybe" },
        { value: "D", label: "D. None of the above" },
      ],
      correct: "B",
    },
    {
      text: "4. It is permissible to gift or donate a good after executing the sale even without taking posession of it.",
      options: [
        { value: "A", label: "A. True" },
        { value: "B", label: "B. False" },
        { value: "C", label: "C. Maybe" },
        { value: "D", label: "D. None of the above." },
      ],
      correct: "A",
    },
  ];

  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
        <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={5}
        timerMinutes={60}
        currentLevel={1}
      />

      <p className="content">
       Welcome to <b>Day 5</b>, where we explore one of the most essential ethical pillars of Islamic commerce: the prohibition of disposing of goods before taking rightful possession. This principle, known as Nāhī an at-Taṣarruf qabla al-Qabḍ, is far more than a technical legal rule, it is a moral safeguard designed to preserve fairness, prevent ambiguity, and ensure that every transaction reflects trust, clarity, and spiritual accountability. Today’s lesson will help you understand how this principle protects wealth, aligns ownership with responsibility, and upholds the integrity of trade in both classical and modern contexts.<br/>
        In Islamic faith, ethical conduct and proper sequencing in transactions
        are not merely procedural details but foundational principles that
        uphold justice, trust, and social harmony. The prohibition of disposing
        of goods before taking possession, known in Arabic as Nāhī an
        at-Taṣarruf qabla al-Qabḍ, occupies a central position within these
        principles. This prohibition is more than a technical rule governing
        transactions; it represents a moral safeguard that ensures clarity,
        prevents exploitation, and protects the mutual rights of both buyer and
        seller. Islamic jurisprudence emphasizes that a transaction is not only
        about transferring ownership but also about maintaining fairness,
        avoiding ambiguity, and ensuring that no party bears an undue burden or
        risk. By understanding this principle, one gains insight into the
        broader ethical framework of Islamic trade, where spiritual
        accountability and material exchange coexist harmoniously.
        <br />
        The prohibition against disposing of goods before possession arises from
        the larger objective of Islamic law known as Maqasid al-Shariah, which
        aims to preserve wealth, life, intellect, faith, and lineage. Within the
        context of commercial dealings, this rule contributes directly to the
        preservation of wealth and fairness by ensuring that property rights are
        clearly defined and properly transferred. In a market system guided by
        divine law, ownership is not a mere legal title but a responsibility
        that carries moral weight. The individual who owns a commodity assumes
        both its benefits and its risks. Hence, Islamic law insists that before
        a person can sell or use what they have purchased, they must first
        establish possession, either physically or constructively. This
        establishes a clear line of accountability, for once possession is
        taken, the buyer becomes responsible for the good and any loss or damage
        that might occur thereafter. Before that point, the seller remains
        liable, since the good is still under his control and within his sphere
        of risk.
        <br />
        The concepts of Taṣarruf and Qabḍ form the core of this discussion.
        Taṣarruf refers to any act of dealing, using, consuming, disposing or
        transferring an item, whether by sale, lease, or personal use. Qabḍ
        refers to possession, which may be physical, such as holding or storing
        an item, or constructive, where the item has come under the control or
        effective authority of the buyer even if it is not physically in their
        hands. For example, if a buyer purchases grain that remains stored in
        the seller’s warehouse, it must be trasnsferred out of the seller's
        warehouse and shifted to the buyer’s residence (assuming the buyer has
        purchased for personal use of consumption) such that he can take
        physical possession of it.
        Similarly, in modern contexts such as digital assets, constructive
        possession may occur when the buyer has been granted legal and practical
        control over the asset. However, if neither physical nor constructive
        possession has occurred, the buyer cannot legally or ethically sell,
        lease, or otherwise dispose of the item, for doing so would involve
        transferring something they do not yet truly own.
        <br />
        <br />
        <b>Scriptural and Prophetic Foundations of the Principle</b>
        <br />
        <br />
        The Qur’anic injunctions on fairness in trade provide the ethical
        foundation for this rule. Allah commands believers to engage only in
        transactions conducted by mutual consent and to refrain from devouring
        one another’s wealth unjustly. Allah says in the Qur'an: <br />
        "O believers! Do not devour one another’s wealth illegally, but rather
        trade by mutual consent. And do not kill ˹each other or˺ yourselves.
        Surely Allah is ever Merciful to you."
        <a href="https://quran.com/4/29" target="blank">
          {" "}
          Surah Al-Nisa (4:29)
        </a>
        <br /> This general principle serves as the bedrock of commercial
        justice in Islam. It calls for transparency, clarity, and sincerity in
        dealings, all of which are compromised when goods are sold or
        transferred before the buyer has taken possession. Similarly, the
        guidance that believers should record debts and clearly define terms in
        transactions demonstrates the Shariah’s emphasis on documentation,
        responsibility, and avoidance of ambiguity. Allah says in the Qur'an :
        <br /> "If you are on a journey and a scribe cannot be found, then a
        security can be taken. If you trust one another, then ˹there is no need
        for a security, but˺ the debtor should honour this trust ˹by repaying
        the debt˺—and let them fear Allah, their Lord. And do not conceal the
        testimony, for whoever conceals it, their hearts are indeed sinful. And
        Allah ˹fully˺ knows what you do."
        <a href="https://quran.com/2/283" target="blank">
           Surah Al-Baqarah (2:283)
        </a>
        <br />
        By preventing disposal before possession, Islamic law ensures that
        ownership and liability are clearly aligned, leaving no room for
        disputes arising from uncertainty over who bears the risk of loss or who
        has rightful claim over the good.
        <br />
        The sayings of the Prophet Muhammad, (peace be upon him) reinforce and
        clarify these principles in practical terms.
        <br />
        Among the most cited narrations is his instruction that one who buys
        food should not sell it until they have taken possession of it.
        <br />
        "He who buys foodgrain should not sell it until he has taken possession
        of it."{" "}
        <a href="https://sunnah.com/muslim:1525a" target="blank">
          (Sahih Muslim 1525)
        </a>
        <br />
        This hadith, reported in Sahih Muslim, directly forbids selling
        something before it has entered the possession of the buyer, thus
        ensuring that trade remains based on clear ownership. The Prophet’s
        teachings frequently emphasized removing causes of dispute and hardship
        from trade. By requiring possession before resale or use, he eliminated
        a major source of ambiguity that could lead to injustice or deception.
        In another narration, the Prophet is reported to have said,
        <br />
        “Do not sell what you do not possess.”
        <a href="https://sunnah.com/abudawud:3503" target="blank">
          (Sunan Abi Dawud 3503)
        </a>
        <br /> This concise instruction captures the spirit of Islamic commerce:
        ownership entails both rights and responsibilities, and these must not
        be separated or manipulated for speculative gain.
        <br />
        The principle of liability transfer after possession further clarifies
        the importance of Nāhī an at-Taṣarruf qabla al-Qabḍ. In Islamic law,
        risk and reward are inseparable; one who expects to benefit from
        ownership must also be prepared to bear its potential losses. Hence,
        liability remains with the seller until the buyer takes possession. If
        the goods are destroyed before delivery, the loss falls on the seller
        because the buyer has not yet assumed control. Conversely, once
        possession is taken, the buyer assumes the risk. This rule maintains
        balance and justice, ensuring that no one profits from what they have
        not yet earned responsibility for. The classical jurists extended this
        principle to all categories of goods, whether perishable or durable,
        movable or immovable. The reasoning is consistent: a transaction should
        not lead to injustice, confusion, or conflict. By establishing a clear
        moment at which ownership and liability shift, the law creates a fair
        and transparent structure for trade.
        <br />
        The scope of this rule is wide and continues to find relevance in modern
        commercial life. In real estate transactions, for example, the sale of a
        property is not complete until the buyer has received the deed or title
        and the transfer has been legally recorded. Selling a property before
        this step would amount to selling something one does not yet own,
        violating the principle of possession before disposal. In e-commerce,
        where goods are ordered online, the buyer cannot ethically sell or
        transfer the purchased goods until they have been delivered and
        received. Similarly, in commodity markets, traders cannot sell goods
        that are still in transit or yet to be delivered, for doing so would
        involve uncertainty about possession and control. The same reasoning
        applies to Bitcoin, where constructive possession is determined by
        control of private keys or access credentials. The underlying principle
        remains constant across centuries: ownership must be real, not
        speculative, and the right to dispose of property must follow
        possession, not precede it.
        <br />
        <br />
        <b>Juristic Interpretations, Modern Applications, and Liability</b>
        <br />
        <br />
        Islamic jurists, through centuries of scholarship, have discussed the
        nuances of what constitutes valid possession. The Hanafi school, for
        instance, holds that possession occurs when the buyer can physically or
        effectively control the good without hindrance from the seller. The
        Shafi‘i and Maliki schools also recognize constructive possession,
        particularly in cases where the good is bulky or stored elsewhere,
        provided the buyer has acquired the right to take it at will. The
        Hanbali school similarly maintains that possession can be established
        through the delivery of keys, documents, or any means that transfers
        control. Despite differences in detail, all scholars agree on
        the principle that possession must precede disposal. Their consensus
        underscores the centrality of this rule in maintaining the integrity of
        Islamic trade ethics.
        <br />
        The juristic rationale for this prohibition lies in the avoidance of
        gharar, or uncertainty. Gharar refers to excessive ambiguity or risk
        that can lead to dispute or injustice. When a person sells what they
        have not yet possessed, the buyer of that second transaction enters into
        an agreement clouded by uncertainty: Will the seller actually receive
        the goods? Are the goods intact, and in whose custody are they? Such
        questions create instability in the marketplace and undermine the trust
        that is vital for healthy trade. By prohibiting transactions before
        possession, Islamic law removes this uncertainty, thus promoting
        stability and predictability in commercial relations. This approach
        contrasts with speculative or derivative trading practices common in
        modern financial systems, which often separate ownership from risk and
        contribute to volatility and moral hazard.
        <br />
        The rule also reflects the ethical spirit of Islam, which discourages
        greed and encourages sincerity and accountability. Selling what one does
        not own or possess can be a form of deception, even if unintended. The
        Prophet (peace be upon him), said that honesty brings blessings while
        deceit destroys them. The prohibition of disposal before possession thus
        protects against deceit and exploitation by ensuring that sellers and
        buyers deal in what they genuinely control and understand. It prevents
        people from making promises they cannot keep or profiting from goods
        they have no responsibility over. In a moral sense, it aligns trade with
        the Islamic virtue of amanah, or trustworthiness, which is integral to
        every lawful transaction.
        <br />
        The application of this rule extends beyond traditional marketplaces. In
        modern commercial settings, it guides ethical business practices in
        supply chains, warehousing, logistics, and digital marketplaces. For
        example, an online retailer who sells products that are not yet in their
        inventory violates the spirit of this rule. They should first ensure
        that the goods are within their possession or control, either directly
        or through a recognized third-party arrangement that establishes
        constructive possession. In contract manufacturing, where a company
        sells goods produced by another entity, clear terms must define when
        ownership transfers and who bears liability for damage or loss during
        production or shipment.
        <br />
        The matter of liability and compensation offers further depth to the
        discussion. If goods are destroyed before possession, the seller remains
        liable, as the transfer of risk has not yet occurred. If the destruction
        occurs due to natural causes such as a flood or earthquake, and the
        goods were still under the seller’s control, the sale is annulled. This
        ensures that neither party bears an unjust burden. In cases where damage
        is caused by a third party, the buyer has the right to either annul the
        sale or seek compensation from the responsible party. The law also
        accounts for situations where the seller has fulfilled their obligation
        to deliver, but the buyer delays in taking possession. In such cases,
        the risk transfers to the buyer, as the delay arises from their
        negligence. This balanced approach reflects the fairness of Islamic law,
        which considers intent, control, and responsibility in determining
        liability.
        <br />
        <br />
        <b>Exceptions, Ethical Reflections, and Contemporary Relevance</b>
        <br />
        <br />
        There are limited exceptions to this rule, primarily grounded in the
        intention behind the transaction. For instance, if a person purchases
        goods solely to donate or gift them without seeking personal gain, some
        scholars permit transferring ownership before possession. The reasoning
        here is that the essence of gifting lies in altruism, not profit, and
        the risk of injustice is minimal. However, even in such cases, scholars
        advise caution to avoid disputes or confusion. These exceptions
        demonstrate the flexibility of Islamic jurisprudence, which adapts the
        law to circumstances while preserving its ethical foundation.
        <br />
        In the modern era, the relevance of Nāhī an at-Taṣarruf qabla al-Qabḍ
        has grown as commercial transactions have become increasingly complex
        and abstract. With the rise of e-commerce, digital assets, and
        international trade, questions of possession, ownership, and liability
        are more important than ever. Islamic law provides timeless guidance by
        emphasizing substance over form: what matters is who truly controls and
        bears responsibility for a good. In futures trading, for example,
        contracts are often made for goods not yet produced or possessed. Such
        arrangements would be impermissible under Islamic law because they
        involve selling the unknown and transferring ownership without
        possession. Similarly, speculative trading in financial markets, where
        assets are bought and sold purely for price fluctuations without ever
        being possessed, contradicts this principle. Islamic commercial ethics
        demand that trade be tied to real economic activity, grounded in actual
        ownership, possession, and productive exchange.
        <br />
        Ultimately, the rule of not disposing of goods before taking possession
        is a reflection of Islam’s holistic approach to economic justice. It
        ensures that trade remains a means of mutual benefit, not exploitation.
        It protects market participants from uncertainty and deceit while
        fostering a culture of trust and accountability. Every transaction in
        Islam is seen not just as a worldly act but as an act of worship,
        subject to divine scrutiny. Observing this principle, therefore, is not
        only a legal obligation but also a spiritual discipline that nurtures
        honesty and contentment. It reminds believers that wealth is a trust
        from Allah, to be earned and exchanged in ways that uphold fairness and
        moral responsibility.
        <br />
        <br />
        <br />
      </p>

      <table class="trade-table">
        <tr>
          <th>
            {" "}
            Liability and Compensation: Determining Responsibility in Various
            Scenarios
          </th>
        </tr>
        <tr>
          <td>
            If the goods get destroyed or lost before the buyer has taken
            posession, then the seller is liable to either give a substitute or
            a compensation to the buyer.
          </td>
        </tr>
        <tr>
          <td>
            If the destruction occured while the good was in posession of the
            seller, however, the cause of destruction was a natural calamity
            beyond the scope of the buyer's control, then the trade will be
            annuled. <br />
            Eg: Salt getting destroyed due to a flood.
          </td>
        </tr>
        <tr>
          <td>
            If the destruction occured while the good was in posession of the
            seller, however, the source of destruction is such that it cannot be
            held liable for a compensation, then the ruling for it is the same
            as that of the good being destroyed by a natural disaster i.e the
            trade is annuled. <br />
            Eg: An animal destroying vegetables that were sold and were ready to
            be dispatched.
          </td>
        </tr>
        <tr>
          <td>
            It the good has been destroyed while it was in the posession of the
            seller, however, by someone other than the seller who can be held
            liable, then the buyer has the right to either annul the trade or
            continue with it, in which case the one who caused the destruction
            is liable to either substitute the good that he destroyed or offer a
            compensation to the buyer.
            <br />
            Eg: An electronic product gets damaged by the delivery person in
            transit.
          </td>
        </tr>
        <tr>
          <td>
            If the seller is ready to hand over possesion of the good but the
            buyer delays it, and in the mean time the product gets damaged,
            destroyed or lost while in the posession of the seller, in this
            scenario the seller is not liable to compensate.
          </td>
        </tr>
      </table>

      <p className="content">
        <br />
        <br /> <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Today's lesson focused on the rule that one cannot sell or transfer
        ownership of an item before physically or constructively taking
        possession of it. This ruling is grounded in several authentic Hadiths,
        including the Prophet Muhammad’s (peace be upon him) instruction not to
        sell what one does not own or possess.
        <br />
        The prohibition aims to eliminate uncertainty (gharar) and reduce the
        risk of dispute or deception in business transactions. It applies to
        both movable and immovable goods, and its implications extend to
        contemporary forms of trade such as online sales and futures contracts.
        <br />
        <br />
        <b>Coming up next</b>
        <br />
        <br />
        In our next session, we will delve into one of the most critical aspects
        of Islamic law i.e the prohibition of Ribā, often translated as usury or
        interest. This concept stands at the heart of Islamic financial ethics
        and is repeatedly emphasized in both the Qur’an and Sunnah.
        <br />
        We will explore what Ribā truly means in Islamic jurisprudence, the
        different forms it can take, and why it is considered so harmful to
        individuals and society. The upcoming lesson will also shed light on how
        this prohibition promotes fairness, prevents exploitation, and
        encourages a just and equitable economic system grounded in real value
        and mutual cooperation.
        <br />
        As we move forward, this topic will challenge us to think deeply about
        modern financial practices and how Islamic teachings offer a principled
        and compassionate alternative.
      </p>

      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

     
      <FloatingPageNav 
        prevLink={links.prev} 
        indexLink={links.index} 
        nextLink={links.next} 
      />
    </div>
  );
}

export default IslamicTradePage5;
